/**
 * Service worker registration, and getting a new version onto the screen.
 *
 * `vite-plugin-pwa`'s generated registration only calls `register()`. That
 * installs a new worker and — because the worker is built with `skipWaiting`
 * and `clientsClaim` — silently swaps it in behind the page that is already
 * rendered. Nothing reloads, so the staff member keeps looking at the OLD
 * build. Closing and reopening the app shows the new one only on the SECOND
 * relaunch, which reads exactly like "the update did not work".
 *
 * Worse, the swap is not harmless: the old page can then ask for a lazy chunk
 * that the new precache no longer contains, and the portal fails to open until
 * something reloads it.
 *
 * So we reload the page ourselves when a new worker takes control. The only
 * question is WHEN, because a front desk PC is open all day and yanking the
 * page out from under someone half way through entering a subscription would
 * lose the form. The rule below is: reload instantly if nobody can be typing,
 * otherwise wait until they look away.
 */

/** How often to ask the server whether a new build exists. */
const UPDATE_CHECK_MS = 30 * 60 * 1000;

/**
 * A page younger than this was almost certainly just launched, so there is no
 * work in progress and reloading is invisible. This is the case that matters:
 * it is what makes closing and reopening the app actually update it.
 */
const JUST_LAUNCHED_MS = 10_000;

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  /**
   * Whether this page was already under a worker's control when it loaded.
   *
   * On a first-ever visit there is no controller, and `clientsClaim` fires
   * `controllerchange` as soon as the very first worker activates. Reloading
   * there would be a pointless flash on every new install, so it is ignored —
   * only a change FROM one worker TO another means the code actually moved on.
   */
  const hadController = Boolean(navigator.serviceWorker.controller);
  const loadedAt = Date.now();

  let reloading = false;
  let updatePending = false;

  const reload = () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  };

  /**
   * Reload now if it cannot interrupt anyone: the page has just launched, or
   * it is not the visible tab. Otherwise remember, and take the next moment
   * the window is hidden — the staff member has walked away or switched
   * app, and will come back to the new version already loaded.
   */
  const reloadWhenSafe = () => {
    if (Date.now() - loadedAt < JUST_LAUNCHED_MS || document.visibilityState === 'hidden') {
      reload();
      return;
    }
    updatePending = true;
  };

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) return;
    reloadWhenSafe();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && updatePending) reload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        /**
         * Ask for a new build periodically and whenever the window regains
         * focus. Without this a machine that is never closed — which is what a
         * front desk PC is — would only notice a deploy on its next restart.
         *
         * Offline is the normal state here, so the check is skipped rather
         * than left to fail; `update()` on a dead network just logs noise.
         */
        const check = () => {
          if (navigator.onLine) void registration.update().catch(() => {});
        };

        setInterval(check, UPDATE_CHECK_MS);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') check();
        });
        window.addEventListener('online', check);
      })
      .catch((e: unknown) => {
        // A failed registration must never stop the app booting — it only
        // costs offline support, and the UI works from Dexie either way.
        console.warn('[pwa] service worker registration failed', e);
      });
  });
}
